import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLocale } from '../../contexts/LocaleContext';
import { API_BASE } from '../../config';
import { Button } from '../ui/button';
import {
  ADMIN_LABEL_CLASSNAME,
  ADMIN_TABLE_LINK_CLASSNAME,
  AdminLoadingState,
} from './admin-ui';

interface StyleNotesScoreBreakdown {
  rejections: number;
  acceptances: number;
  reports: number;
  styleNotesActiveBonus: number;
}

interface StyleNotesCalibrationEvaluation {
  shouldRegenerate: boolean;
  outcome: string | null;
  calibrationScore: number;
  threshold: number;
  breakdown: StyleNotesScoreBreakdown;
  stylisticClosesSinceUpdate: number;
  acceptanceRate: number | null;
  debounceExpiresAt: string | null;
  status: 'stable' | 'accumulating' | 'debounced' | 'ready';
  stabilityGuardActive: boolean;
}

interface StyleNotesRecentClose {
  id: string;
  closedAt: string | null;
  status: string;
  issueType: string;
  originalText: string;
  pointsContributed: number;
  styleNotesActiveAtGeneration: boolean;
  isStylistic: boolean;
}

interface StyleNotesRegenerationLogEntry {
  id: string;
  outcome: string;
  calibrationScore: number;
  threshold: number;
  llmCallId: string | null;
  createdAt: string;
}

interface StyleNotesCalibrationResponse {
  styleNotes: string | null;
  styleNotesUpdatedAt: string | null;
  evaluation: StyleNotesCalibrationEvaluation;
  recentCloses: StyleNotesRecentClose[];
  regenerationHistory: StyleNotesRegenerationLogEntry[];
  config: {
    rejectionPoints: number;
    acceptancePoints: number;
    reportPoints: number;
    styleNotesActiveBonus: number;
  };
}

interface StyleNotesCalibrationPanelProps {
  documentId: string;
  onStyleNotesUpdated?: (styleNotes: string | null) => void;
}

const statusColors: Record<string, string> = {
  stable: '#10b981',
  accumulating: '#6b7280',
  debounced: '#f59e0b',
  ready: '#2563eb',
};

export const StyleNotesCalibrationPanel: React.FC<StyleNotesCalibrationPanelProps> = ({
  documentId,
  onStyleNotesUpdated,
}) => {
  const { token, masterKey } = useAuth();
  const { t } = useLocale();
  const [data, setData] = useState<StyleNotesCalibrationResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [forcing, setForcing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchCalibration = useCallback(async () => {
    if (!token && !masterKey) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const url = masterKey
        ? `${API_BASE}/api/admin/style-notes/documents/${documentId}/calibration?masterKey=${encodeURIComponent(masterKey)}`
        : `${API_BASE}/api/admin/style-notes/documents/${documentId}/calibration`;

      const response = await fetch(url, {
        headers: token && !masterKey ? { Authorization: `Bearer ${token}` } : {},
      });

      if (!response.ok) {
        throw new Error('Failed to load calibration data');
      }

      const json = await response.json() as StyleNotesCalibrationResponse;
      setData(json);
    } catch (err) {
      console.error('Error loading style notes calibration:', err);
      setError(t('admin_style_notes_calibration_load_error'));
    } finally {
      setLoading(false);
    }
  }, [documentId, masterKey, t, token]);

  useEffect(() => {
    void fetchCalibration();
  }, [fetchCalibration]);

  const regenerate = async (force: boolean) => {
    if ((!token && !masterKey) || regenerating || forcing) {
      return;
    }

    if (force) {
      const confirmed = window.confirm(t('admin_style_notes_force_confirm'));
      if (!confirmed) {
        return;
      }
    }

    if (force) {
      setForcing(true);
    } else {
      setRegenerating(true);
    }

    try {
      const params = new URLSearchParams();
      if (force) {
        params.set('force', 'true');
      }
      if (masterKey) {
        params.set('masterKey', masterKey);
      }

      const url = `${API_BASE}/api/documents/${documentId}/style-notes/regenerate?${params.toString()}`;
      const response = await fetch(url, {
        method: 'POST',
        headers: token && !masterKey ? { Authorization: `Bearer ${token}` } : {},
      });

      if (!response.ok) {
        throw new Error('Failed to regenerate style notes');
      }

      const result = await response.json();
      if (result.styleNotes !== undefined) {
        onStyleNotesUpdated?.(result.styleNotes);
      }

      await fetchCalibration();
    } catch (err) {
      console.error('Error regenerating style notes:', err);
      alert(t('admin_style_notes_regenerate_error'));
    } finally {
      setRegenerating(false);
      setForcing(false);
    }
  };

  if (loading) {
    return <AdminLoadingState label={t('admin_style_notes_calibration_loading')} />;
  }

  if (error || !data) {
    return (
      <p className="m-0 text-sm text-destructive">
        {error || t('admin_style_notes_calibration_load_error')}
      </p>
    );
  }

  const { evaluation, config } = data;
  const { breakdown } = evaluation;
  const progress = Math.min((evaluation.calibrationScore / evaluation.threshold) * 100, 100);

  return (
    <div className="mt-4 border-t border-border pt-4">
      <h4 className={`mb-3 ${ADMIN_LABEL_CLASSNAME}`}>
        {t('admin_style_notes_calibration_title')}
      </h4>

      <div className="mb-4 grid gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <span
            className="inline-block rounded-full px-2 py-0.5 text-xs font-semibold text-white"
            style={{ backgroundColor: statusColors[evaluation.status] || '#6b7280' }}
          >
            {t(`admin_style_notes_status_${evaluation.status}`)}
          </span>
          <span className="text-sm text-foreground">
            {evaluation.calibrationScore} / {evaluation.threshold}
          </span>
          {data.styleNotesUpdatedAt && (
            <span className="text-xs text-muted-foreground">
              {t('admin_style_notes_last_updated')}: {new Date(data.styleNotesUpdatedAt).toLocaleString()}
            </span>
          )}
        </div>

        <div className="h-2 overflow-hidden rounded-full bg-muted">
          <div
            className="h-full transition-[width] duration-200 ease-out"
            style={{
              width: `${progress}%`,
              backgroundColor: statusColors[evaluation.status] || '#6b7280',
            }}
          />
        </div>

        {evaluation.debounceExpiresAt && evaluation.status === 'debounced' && (
          <p className="m-0 text-xs text-amber-700 dark:text-amber-400">
            {t('admin_style_notes_debounce_until')}: {new Date(evaluation.debounceExpiresAt).toLocaleString()}
          </p>
        )}
      </div>

      <table className="mb-4 w-full border-collapse text-[0.8125rem] text-foreground">
        <thead>
          <tr className="border-b border-border">
            <th className="py-1.5 text-left font-medium text-muted-foreground">{t('admin_style_notes_breakdown_source')}</th>
            <th className="py-1.5 text-right font-medium text-muted-foreground">{t('admin_style_notes_breakdown_count')}</th>
            <th className="py-1.5 text-right font-medium text-muted-foreground">{t('admin_style_notes_breakdown_points')}</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="py-1.5">{t('admin_style_notes_breakdown_rejections')}</td>
            <td className="text-right">{breakdown.rejections}</td>
            <td className="text-right">{breakdown.rejections * config.rejectionPoints}</td>
          </tr>
          <tr>
            <td className="py-1.5">{t('admin_style_notes_breakdown_acceptances')}</td>
            <td className="text-right">{breakdown.acceptances}</td>
            <td className="text-right">{breakdown.acceptances * config.acceptancePoints}</td>
          </tr>
          <tr>
            <td className="py-1.5">{t('admin_style_notes_breakdown_reports')}</td>
            <td className="text-right">{breakdown.reports}</td>
            <td className="text-right">{breakdown.reports * config.reportPoints}</td>
          </tr>
          <tr>
            <td className="py-1.5">{t('admin_style_notes_breakdown_active_bonus')}</td>
            <td className="text-right">{breakdown.styleNotesActiveBonus}</td>
            <td className="text-right">{breakdown.styleNotesActiveBonus * config.styleNotesActiveBonus}</td>
          </tr>
        </tbody>
      </table>

      {data.recentCloses.length > 0 && (
        <div className="mb-4">
          <h5 className={`mb-2 text-[0.8125rem] ${ADMIN_LABEL_CLASSNAME}`}>
            {t('admin_style_notes_recent_closes')}
          </h5>
          <div className="max-h-[180px] overflow-auto text-xs">
            {data.recentCloses.map((close) => (
              <div key={close.id} className="border-b border-border py-1.5">
                <div className="text-muted-foreground">
                  {close.closedAt ? new Date(close.closedAt).toLocaleString() : '-'} · {close.status} · {close.issueType} · +{close.pointsContributed}
                </div>
                <div className="break-words text-foreground">
                  {close.originalText.length > 120 ? `${close.originalText.slice(0, 120)}…` : close.originalText}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {data.regenerationHistory.length > 0 && (
        <div className="mb-4">
          <h5 className={`mb-2 text-[0.8125rem] ${ADMIN_LABEL_CLASSNAME}`}>
            {t('admin_style_notes_regeneration_history')}
          </h5>
          <div className="text-xs text-muted-foreground">
            {data.regenerationHistory.map((entry) => (
              <div key={entry.id} className="py-1">
                {new Date(entry.createdAt).toLocaleString()} · {entry.outcome} · {entry.calibrationScore}/{entry.threshold}
                {entry.llmCallId && (
                  <>
                    {' · '}
                    <Link to={`/admin/llm-calls?callId=${entry.llmCallId}`} className={ADMIN_TABLE_LINK_CLASSNAME}>
                      LLM
                    </Link>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          onClick={() => void regenerate(false)}
          disabled={regenerating || forcing}
        >
          {regenerating ? t('admin_style_notes_regenerating') : t('admin_style_notes_regenerate')}
        </Button>
        <Button
          type="button"
          variant="destructive"
          onClick={() => void regenerate(true)}
          disabled={regenerating || forcing}
        >
          {forcing ? t('admin_style_notes_regenerating') : t('admin_style_notes_force_regenerate')}
        </Button>
      </div>
    </div>
  );
};
